import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionTemplate, useMotionValue, useSpring } from "framer-motion";
import { useLanguage, productCopy } from "../i18n";

const FRUIT_IMG =
  "https://images.unsplash.com/photo-1734894966816-9f2b7bf9c605?q=85&w=1400&auto=format&fit=crop";

const FEATURE_POS = [
  "left-[4%] top-1/2 -translate-y-1/2 max-w-[40vw] text-left sm:left-[7%] sm:max-w-[250px]",
  "right-[4%] top-1/2 -translate-y-1/2 max-w-[40vw] text-right sm:right-[7%] sm:max-w-[250px]",
  "bottom-[9%] left-1/2 -translate-x-1/2 max-w-[82vw] text-center sm:max-w-md",
];

export const ProductSpotlight = () => {
  const { lang } = useLanguage();
  const c = productCopy[lang] || productCopy.tr;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  const scrollRotate = useTransform(scrollYProgress, [0, 1], [-16, 22]);
  const dragRotate = useMotionValue(0);
  const dragSpring = useSpring(dragRotate, { stiffness: 140, damping: 22 });
  const rotate = useTransform([scrollRotate, dragSpring], ([a, b]) => a + b);

  const scale = useTransform(scrollYProgress, [0, 0.45, 1], [0.7, 1.12, 0.92]);
  const fruitY = useTransform(scrollYProgress, [0, 1], ["5%", "-5%"]);
  const fruitOpacity = useTransform(scrollYProgress, [0, 0.07], [0, 1]);
  const glowX = useTransform(scrollYProgress, [0, 1], [25, 75]);
  const glow = useMotionTemplate`radial-gradient(circle at ${glowX}% 38%, rgba(72,227,142,0.17), transparent 55%)`;

  const introO = useTransform(scrollYProgress, [0.02, 0.07, 0.11, 0.15], [0, 1, 1, 0]);
  const introY = useTransform(scrollYProgress, [0.02, 0.07, 0.11, 0.15], [24, 0, 0, -24]);

  const featsO = useTransform(scrollYProgress, [0.28, 0.38, 0.88, 0.96], [0, 1, 1, 0]);
  const featsY = useTransform(scrollYProgress, [0.28, 0.38, 0.88, 0.96], [24, 0, 0, -24]);

  const dragging = useRef(false);
  const lastX = useRef(0);
  const onPointerDown = (e) => {
    dragging.current = true;
    lastX.current = e.clientX;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    dragRotate.set(dragRotate.get() + (e.clientX - lastX.current) * 0.4);
    lastX.current = e.clientX;
  };
  const onPointerUp = () => {
    dragging.current = false;
  };

  return (
    <section ref={ref} data-testid="product-spotlight" className="relative h-[420vh] bg-[#04120b]">
      <div className="sticky top-0 flex h-screen flex-col items-center justify-center overflow-hidden">
        <motion.div style={{ background: glow }} className="absolute inset-0" aria-hidden="true" />
        <div className="absolute h-[72vmin] w-[72vmin] rounded-full border border-[#48e38e]/10" aria-hidden="true" />
        <div className="absolute h-[92vmin] w-[92vmin] rounded-full border border-[#48e38e]/5" aria-hidden="true" />

        <motion.img
          src={FRUIT_IMG}
          alt={c.caption}
          draggable={false}
          data-testid="product-fruit"
          data-cursor
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{ rotate, scale, y: fruitY, opacity: fruitOpacity }}
          className="relative z-10 aspect-square w-[min(60vmin,540px)] cursor-grab touch-pan-y select-none rounded-full object-cover mix-blend-lighten drop-shadow-[0_50px_90px_rgba(72,227,142,0.18)] will-change-transform [mask-image:radial-gradient(circle,black_52%,transparent_71%)] active:cursor-grabbing"
        />

        <motion.div style={{ opacity: introO, y: introY }} className="absolute top-[13%] z-20 px-6 text-center">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.35em] text-[#48e38e]">{c.overline}</p>
          <p className="font-display mt-3 text-base text-white/55 sm:text-lg">{c.caption}</p>
        </motion.div>

        {c.features.map((f, i) => (
          <motion.div
            key={f.title}
            data-testid={`product-feature-${i}`}
            style={{ opacity: featsO, y: featsY }}
            className={`pointer-events-none absolute z-20 ${FEATURE_POS[i]}`}
          >
            <h3 className="font-display text-base font-bold tracking-tight text-white sm:text-2xl">
              {f.title}
            </h3>
            <p className="mt-1.5 text-[11px] leading-relaxed text-white/55 sm:mt-3 sm:text-sm">{f.sub}</p>
          </motion.div>
        ))}

        <motion.div
          style={{ scaleX: scrollYProgress }}
          className="absolute bottom-10 left-1/2 z-20 h-px w-44 origin-left -translate-x-1/2 bg-[#48e38e]/70"
          aria-hidden="true"
        />
      </div>
    </section>
  );
};
